/**
 * Deterministic checks that a ballot is constructive. No AI, works offline.
 * These are nudges; nothing blocks submission.
 */
export interface BallotCheck {
  id: string;
  ok: boolean;
  label: string;
}

export interface BallotCheckInput {
  rfd: string;
  winnerLabel: string | null;
  winnerCode: string | null;
  loserLabel: string | null;
  teamComments: string[];
  speakerComments: string[];
}

const has = (text: string, re: RegExp) => re.test(text.toLowerCase());

/** Phrases that make feedback read as machine-written. Flag, don't block. */
export const AI_TELLS = ['delve', "it's important to note", 'it is important to note', 'in conclusion', 'tapestry', 'testament to', 'navigate the complexities', 'multifaceted', 'furthermore,', 'moreover,', 'showcasing', 'underscore'];

export const checkBallot = (b: BallotCheckInput): BallotCheck[] => {
  const rfd = b.rfd.trim();
  const all = [rfd, ...b.teamComments, ...b.speakerComments].join('\n').toLowerCase();
  const tells = AI_TELLS.filter((t) => all.includes(t));
  const names = [b.winnerLabel, b.winnerCode].filter(Boolean).map((s) => s!.toLowerCase());
  return [
    { id: 'decision', ok: rfd.length > 0 && names.some((n) => rfd.toLowerCase().includes(n)), label: 'States who won (side or team code)' },
    { id: 'because', ok: has(rfd, /\b(because|since|the reason|what decided)\b/), label: 'Explains why (the argument that decided it)' },
    { id: 'weighing', ok: has(rfd, /\b(outweigh|weigh|magnitude|probability|timeframe|scope|more important|bigger)\b/), label: 'Says why that argument mattered more (weighing)' },
    { id: 'loser', ok: has(all, /\b(next time|could have|should have|to win|would have|try)\b/), label: `Gives ${b.loserLabel ?? 'the losing team'} something concrete to do next time` },
    { id: 'kind', ok: !has(all, /\b(stupid|terrible|awful|pathetic|lazy|dumb|embarrassing)\b/), label: 'Kind tone (these are students)' },
    { id: 'length', ok: rfd.length >= 150, label: 'RFD has enough detail to learn from (150+ characters)' },
    { id: 'voice', ok: tells.length === 0, label: tells.length ? `Sounds like you, not AI (flagged: ${tells.join(', ')})` : 'Sounds like you, not AI' },
  ];
};
